import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Handshake, X, CheckCircle2, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  { value: 'employment',          label: 'Employment assistance / Job readiness' },
  { value: 'mail_drop',           label: 'Mail drop services' },
  { value: 'pfc_id',              label: 'PFC ID' },
  { value: 'homeless_id_waiver',  label: 'Homeless ID waiver assistance (DL or State ID)' },
  { value: 'cha_application',     label: 'Chicago Housing Authority (CHA) application assistance' },
];

const HOUSING = [
  { value: 'unhoused',          label: 'Unhoused' },
  { value: 'at_risk',           label: 'At risk of homelessness' },
  { value: 'securely_housed',   label: 'Securely housed' },
];

const RECORD_OPTIONS = [
  { value: 'yes',                 label: 'Yes' },
  { value: 'no',                  label: 'No' },
  { value: 'prefer_not_to_say',   label: 'Prefer not to say' },
];

const PFCReferralForm = ({ caseId, neighbor, onClose, onSent }) => {
  const [services, setServices] = useState([]);
  const [housingStatus, setHousingStatus] = useState('');
  const [hasRecord, setHasRecord] = useState('');
  const [notes, setNotes] = useState('');
  const [consent, setConsent] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState(neighbor?.email || '');
  const [sending, setSending] = useState(false);

  const toggleService = (val) => {
    setServices(prev => prev.includes(val) ? prev.filter(s => s !== val) : [...prev, val]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (services.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    if (!housingStatus) {
      toast.error('Please select the neighbor\'s housing status');
      return;
    }
    if (!consent) {
      toast.error('Neighbor consent is required before sending');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/cases/${caseId}/pfc-referral`,
        {
          services,
          housing_status: housingStatus,
          has_criminal_record: hasRecord || null,
          additional_notes: notes,
          consent_given: true,
          override_email: overrideEmail || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      let msg = 'PFC Connect referral sent';
      if (res.data.employment_cc_sent) msg += ' (employment contact also notified)';
      if (res.data.neighbor_notified)  msg += '. Neighbor was emailed a copy.';
      toast.success(msg);
      if (onSent) onSent();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to send referral');
    } finally {
      setSending(false);
    }
  };

  const neighborName = `${neighbor?.first_name || ''} ${neighbor?.last_name || ''}`.trim() || '—';
  const neighborPhone = neighbor?.phone || '—';
  const employmentSelected = services.includes('employment');

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="pfc-referral-modal">
      <div className="glass-panel w-full max-w-2xl my-6 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/15 text-emerald-400">
              <Handshake className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">PFC Connect Referral</h2>
              <p className="text-xs text-slate-400">Saint Ita Conference · SVDP-MMOG Assist</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10" data-testid="pfc-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Pre-filled neighbor info */}
          <div className="p-3 rounded-lg bg-white/5 border border-white/10" data-testid="pfc-neighbor-summary">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Neighbor</p>
            <p className="text-sm text-white font-medium">{neighborName}</p>
            <p className="text-xs text-slate-400">Phone: {neighborPhone}</p>
          </div>

          {/* Neighbor email override */}
          <div>
            <label className="block text-sm text-slate-300 mb-1.5">Neighbor email (will be CC'd on referral)</label>
            <input
              type="email"
              value={overrideEmail}
              onChange={(e) => setOverrideEmail(e.target.value)}
              placeholder="neighbor@example.com"
              className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-[#0072BC] outline-none"
              data-testid="pfc-email-input"
            />
            <p className="text-xs text-slate-500 mt-1">If empty, only PFC gets the referral; the neighbor won't be emailed.</p>
          </div>

          {/* Services */}
          <div>
            <label className="block text-sm text-slate-300 mb-2">Services requested <span className="text-rose-400">*</span></label>
            <div className="space-y-2">
              {SERVICES.map(s => (
                <label key={s.value} className="flex items-start gap-2 p-2.5 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors" data-testid={`pfc-service-${s.value}`}>
                  <input
                    type="checkbox"
                    checked={services.includes(s.value)}
                    onChange={() => toggleService(s.value)}
                    className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-[#0072BC] focus:ring-[#0072BC] accent-[#0072BC]"
                  />
                  <span className="text-sm text-white">{s.label}</span>
                </label>
              ))}
            </div>
            {employmentSelected && (
              <div className="mt-2 flex items-start gap-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-200" data-testid="pfc-employment-notice">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Since Employment / Job readiness is selected, an additional notification will be sent to the PFC employment coordinator.</span>
              </div>
            )}
          </div>

          {/* Housing status */}
          <div>
            <label className="block text-sm text-slate-300 mb-2">Housing status <span className="text-rose-400">*</span></label>
            <div className="flex flex-wrap gap-2">
              {HOUSING.map(h => (
                <button
                  type="button"
                  key={h.value}
                  onClick={() => setHousingStatus(h.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                    housingStatus === h.value
                      ? 'bg-[#0072BC] text-white border-[#0072BC]'
                      : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'
                  }`}
                  data-testid={`pfc-housing-${h.value}`}
                >{h.label}</button>
              ))}
            </div>
          </div>

          {/* Record */}
          <div>
            <label className="block text-sm text-slate-300 mb-2">Does the neighbor have a record? <span className="text-slate-500 text-xs">(important for job referrals)</span></label>
            <div className="flex flex-wrap gap-2">
              {RECORD_OPTIONS.map(r => (
                <button
                  type="button"
                  key={r.value}
                  onClick={() => setHasRecord(r.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                    hasRecord === r.value
                      ? 'bg-[#0072BC] text-white border-[#0072BC]'
                      : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'
                  }`}
                  data-testid={`pfc-record-${r.value}`}
                >{r.label}</button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-slate-300 mb-1.5">Additional notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Any context Pope Francis Center should know before reaching out…"
              className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-[#0072BC] outline-none resize-none"
              data-testid="pfc-notes-input"
            />
          </div>

          {/* Consent */}
          <label className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 cursor-pointer" data-testid="pfc-consent-checkbox">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-5 h-5 rounded border-amber-500/40 bg-white/5 text-amber-500 focus:ring-amber-500 accent-amber-500"
            />
            <span className="text-sm text-amber-100">
              <strong>Neighbor has agreed to the referral</strong> and the sharing of this information with Pope Francis Center. <span className="text-rose-400">*</span>
            </span>
          </label>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
            <Button type="button" variant="outline" onClick={onClose} className="border-white/20 text-slate-300" data-testid="pfc-cancel-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pfc-send-btn">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              {sending ? 'Sending…' : 'Send Referral'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PFCReferralForm;
